import {useNavigate, useSearchParams} from 'react-router-dom';
import {GUIDE_KEYS, RUTAS} from "constants/app";
import {useCallback, useEffect, useState} from "react";
import "../styles/WelcomePage.css"
import {useInitializeGuides} from "application/hooks/useInitializeGuides";
import {getFormRepository} from "infrastructure/di/container";
import {useTheme} from "../providers/ThemeProvider";

export default function WelcomePage() {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const urlToken = searchParams.get("token");
    const localToken = localStorage.getItem("user-token");
    const guia = localStorage.getItem("guia");
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768); // Detecta si es móvil al inicio

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const toGuideKey = (guia: String) => {
        switch (guia) {
            case "GUIA_II":
                return GUIDE_KEYS.II;
            case "GUIA_III":
                return GUIDE_KEYS.III;
            default:
                return GUIDE_KEYS.II;
        }
    }

    const handleStart = useCallback(() => {
        navigate(`${RUTAS.FORMULARIO}?token=${urlToken}`);
    }, [navigate, urlToken]);

    useEffect(() => {
        window.history.replaceState(null, '', window.location.href);
        if (urlToken !== localToken) {
            navigate(RUTAS.ACCESO);
        }
    }, [urlToken, localToken, navigate]);

    const {ready, error} = useInitializeGuides(localToken || "", toGuideKey(guia || ""));
    const [retrying, setRetrying] = useState(false);
    const [showErrorMessage, setShowErrorMessage] = useState(true);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
                handleStart();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleStart]);

    const handleRetry = useCallback(async () => {
        setRetrying(true);
        setShowErrorMessage(false);

        try {
            const repo = getFormRepository();
            const guideKey = toGuideKey(guia || '');

            // Cargar en paralelo las guías
            const [guiaI, guiaExtra] = await Promise.all([
                repo.getGuideByKey(GUIDE_KEYS.I, true),
                repo.getGuideByKey(guideKey, true),
            ]);

            const isValid = !!guiaI && !!guiaExtra;
            if (!isValid) {
                setShowErrorMessage(true);
                return;
            }

            navigate(`${RUTAS.FORMULARIO}?token=${urlToken}`);
        } catch (err) {
            console.error('Reintento fallido:', err);
            setShowErrorMessage(true);
        } finally {
            setRetrying(false);
        }
    }, [navigate, urlToken, guia]);

    const subtitle = isMobile
        ? theme?.content.welcomePage.subtitleMobile ?? theme?.content.welcomePage.subtitle
        : theme?.content.welcomePage.subtitle;

    return (
        <div className="welcome-wrapper">
            <div className="welcome-card">
                <div className="welcome-left">
                    <div className="welcome-content">
                        {isMobile && theme?.assets.welcomeMobileIcon && (
                            <img src={theme.assets.welcomeMobileIcon} alt="Icono móvil de bienvenida"
                                 className="welcome-mobile-icon"/>
                        )}
                        <h1 className="welcome-title"
                            dangerouslySetInnerHTML={{__html: theme?.content.welcomePage.title || ''}}></h1>
                        {subtitle && <p className="welcome-nom">{subtitle}</p>}
                        <p className="welcome-description">
                            {theme?.content.welcomePage.description1}
                        </p>
                        <p className="welcome-description">
                            {theme?.content.welcomePage.description2}
                        </p>
                        <button
                            className={`welcome-button ${((error) && !retrying) ? "wide-button" : ""}`}
                            onClick={(error) ? handleRetry : handleStart}
                            disabled={((!ready) && !(error)) || retrying}
                        >
                            {(error) ? (
                                retrying ? (
                                    <span className="preparando-guia">{theme?.content.welcomePage.preparingMessage}{" "}
                                        <span className="loading-dots"><span>.</span><span>.</span><span>.</span></span></span>
                                ) : (
                                    theme?.content.welcomePage.buttonLabelRetry
                                )
                            ) : !ready ? (<span className="preparando-guia">{theme?.content.welcomePage.preparingMessage}{" "}
                                    <span
                                        className="loading-dots"><span>.</span><span>.</span><span>.</span></span></span>
                            ) : (
                                theme?.content.welcomePage.buttonLabelStart
                            )}
                        </button>
                        {(error) && showErrorMessage && (
                            <div className="welcome-icono-error" style={{marginTop: 'var(--spacing-md)'}}>
                                <img src={theme?.assets.iconClose} alt="icono error" className="welcome-icono-error-close"/>
                                <p className="welcome-error-message">
                                    {theme?.content.welcomePage.errorMessage}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
                <div className="welcome-right">
                    <img src={theme?.assets.welcomeImage} alt="Bienvenido" className="welcome-image"/>
                </div>
            </div>
        </div>

    );
}